import { isNil, orderBy, uniqBy } from 'lodash'
import PropTypes from 'prop-types'
import React, { useMemo, useState, useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import Button from 'components/forms/Button'
import Select from 'components/forms/Select'
import ThresholdSelect from 'components/forms/specific/ThresholdSelect'
import DtoOperation from 'quality/dto/operation/DtoOperation'
import SupportAction from '../../../referencial/components/support/actions/SupportAction'
import useListIndexed from 'utils/customHook/useListIndexed'
import { getYear } from 'date-fns'
import { Card, CardContent, Grid } from '@mui/material'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'

const SEUILS_REF = 'SEUILS_REFERENCE'
const SEUILS_LIM = 'SEUILS_LIMITE'

const AEPCriterias = ({
    defaultFilter = {},
    onValidate = () => { },
    operations = [],
}) => {
    const {
        producers,
        supports,
    } = useSelector(store => ({
        producers: store.ContributorReducer.producers,
        supports: store.SupportReducer.supports,
    }), shallowEqual)

    const indexedProducers = useListIndexed(producers, 'id')
    const indexedSupports = useListIndexed(supports, 'id')
    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const dispatch = useDispatch()

    const [producer, setProducer] = useState(defaultFilter.producer)
    const [year, setYear] = useState(defaultFilter.year)
    const [referenceThreshold, setReferenceThreshold] = useState(defaultFilter.referenceThreshold)
    const [limitThreshold, setLimitThreshold] = useState(defaultFilter.limitThreshold)
    const [support, setSupport] = useState(defaultFilter.support)

    useEffect(() => {
        if (!supports.length) {
            dispatch(SupportAction.fetchSupports())
        }
    }, [])

    // années des opérations, plus l'année par défaut (qui peut ne pas encore avoir d'opération)
    const yearList = useMemo(() => {
        const years = uniqBy([...operations.map(o => getYear(o.date)), ...(isNil(defaultFilter.year) ? [] : [defaultFilter.year])], y => y)
        return orderBy(years.map(y => ({ code: y, name: y })), 'code', 'desc')
    }, [operations, defaultFilter.year])

    const producerList = useMemo(() => {
        const uniqIds = uniqBy(operations, 'producer').map(o => o.producer)
        return uniqIds.map(id => {
            const prod = indexedProducers[id]
            if (isNil(prod)) return prod
            return { id, name: `${prod.mnemonique || prod.name || ''} ${showSandreCode ? `[${prod.siret || prod.code}]` : ''}` }
        }).filter(p => !!p)
    }, [indexedProducers, operations, showSandreCode])

    const supportList = useMemo(() => {
        const uniqSupport = uniqBy(operations, 'support').map(o => o.support)
        return uniqSupport.map(id => indexedSupports[id]).filter(p => !!p)
    }, [indexedSupports, operations])

    return (
        <Card>
            <CardContent>
                <Grid container sx={{ paddingTop: '0.5rem' }} rowSpacing={0.5} columnSpacing={1} alignItems='center'>
                    <Grid size={9}>
                        <Select
                            options={producerList}
                            label={i18n.producers}
                            value={producer}
                            nullLabel='&nbsp;'
                            onChange={setProducer}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            options={yearList}
                            label={i18n.year}
                            value={year}
                            onChange={setYear}
                            nullLabel='&nbsp;'
                            noSort
                        />
                    </Grid>
                    <Grid size={3}>
                        <ThresholdSelect
                            title={i18n.referenceThreshold}
                            selected={referenceThreshold}
                            onChange={setReferenceThreshold}
                            fieldRef={SEUILS_REF}
                        />
                    </Grid>
                    <Grid size={3}>
                        <ThresholdSelect
                            title={i18n.limitThreshold}
                            selected={limitThreshold}
                            onChange={setLimitThreshold}
                            fieldRef={SEUILS_LIM}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            label={i18n.support}
                            options={supportList}
                            onChange={setSupport}
                            value={support}
                            keyValue={'id'}
                            displayWithCode={showSandreCode}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Button style={{ width: '100%' }}
                            title={i18n.search}
                            onClick={() => onValidate({
                                producer,
                                year,
                                referenceThreshold,
                                limitThreshold,
                                support,
                            })}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

AEPCriterias.propTypes = {
    onValidate: PropTypes.func,
    defaultFilter: PropTypes.shape({
        producer: PropTypes.number,
        year: PropTypes.number,
        threshold: PropTypes.number,
        support: PropTypes.string,
        filter: PropTypes.number,
        filterResults: PropTypes.arrayOf(PropTypes.number),
    }),
    operations: PropTypes.arrayOf(PropTypes.instanceOf(DtoOperation)),
}

export default AEPCriterias